import Utils from '../../../util/Utils';
import {
  containsNumbers,
  isFirstLetterUppercase,
  leaveNumbers,
  leaveWords,
  splitString,
} from '../../../util/regex';
import { parents } from '../../../util/dom';
import * as JraValidators from './JraValidators';

export const VALIDATION_ERROR_CLASS = 'error';
export const INVALID_FIELD_STRING = 'Invalid field';

function setInputValidationMessage(input, message) {
  const validationLabel = input.parentElement.querySelector('.Form_validation');
  if (validationLabel) {
    validationLabel.innerHTML = message;
  } else {
    throw new Error('Error setting validation message, there is no span in parent with .Form_validation');
  }
}

export function capitalizeInputValue(input) {
  if (!input.value) return;
  // split input value into an array of strings,
  // each string will contain any special chars preceeding a word + spaces after.
  const inputStrings = splitString(input.value);
  const newInputStrings = [];

  inputStrings.forEach((inputString) => {
    // check each string for words & capitalize
    const words = leaveWords(inputString);
    if (words && !isFirstLetterUppercase(words[0])) {
      const word = words[0];
      const newWord = word.replace(word.charAt(0), word.charAt(0).toUpperCase());
      const regExString = `(${word})`;
      const wordRegEx = new RegExp(regExString, 'gi');
      const newInputString = inputString.replace(wordRegEx, newWord);
      newInputStrings.push(newInputString);
    } else {
      newInputStrings.push(inputString);
    }
  });
  input.value = newInputStrings.join('');
}

export function changeInputValueToUppercase(input) {
  input.value = input.value.toUpperCase();
}

export function resetInputValue(input) {
  const isTextOrSelectInput = input.type === 'text' || input.type === 'select-one' || input.type === 'email' || input.type === 'number';
  const isCheckboxOrRadioInput = input.type === 'checkbox' || input.type === 'radio';

  if (isTextOrSelectInput && input.value !== '') {
    input.value = '';
  } else if (isCheckboxOrRadioInput && input.checked) {
    input.checked = false;
  }
}

export function resetInputs(inputs) {
  inputs.forEach((input) => {
    resetInputValue(input);
  });
}

function extractValidatorName(attributeName) {
  let attributeString = attributeName;
  if (containsNumbers(attributeName)) {
    const attributeNumber = leaveNumbers(attributeName);
    const attributeNameRegexString = `[-]|[${attributeNumber}]`;
    const attributeNameRegex = new RegExp(attributeNameRegexString, 'gi');
    attributeString = attributeName.replace(attributeNameRegex, '');
  }
  attributeString = Utils.camelCase(attributeString.split('formValidator').pop());

  return attributeString;
}

export function getFormValidatorNames(dataset) {
  let formValidatorNames;
  const attributeNames = Object.keys(dataset);
  const formValidatorAttributes = attributeNames.filter(name => name.includes('formValidator'));
  const numberedAttributes = [];
  const unnumberedAttributes = [];

  formValidatorAttributes.forEach((attribute) => {
    let attributeName;

    if (containsNumbers(attribute)) {
      const attributeNumber = leaveNumbers(attribute);
      attributeName = extractValidatorName(attribute);

      const attributeObject = {
        key: attributeNumber,
        value: attributeName,
      };

      numberedAttributes.push(attributeObject);
    } else {
      // handling for any unnumbered attributes
      attributeName = extractValidatorName(attribute);
      unnumberedAttributes.push(attributeName);
    }
  });

  if (numberedAttributes.length > 0) {
    const sortedAttributes = [];
    const sortedNumbers = Object.keys(numberedAttributes).sort((a, b) => a - b);

    sortedNumbers.forEach((number) => {
      numberedAttributes.forEach((attribute) => {
        if (attribute.key === number) {
          sortedAttributes.push(attribute.value);
        }
      });
    });

    if (unnumberedAttributes.length > 0) {
      formValidatorNames = sortedAttributes.concat(unnumberedAttributes);
    } else {
      formValidatorNames = sortedAttributes;
    }
  } else {
    formValidatorNames = unnumberedAttributes;
  }

  return formValidatorNames;
}

export function findAttributeName(input, validatorName) {
  const attributes = Object.keys(input.dataset).filter(attribute => attribute.includes('formValidator'));
  let attributeName;

  for (let index = 0; index < attributes.length; index += 1) {
    const attribute = attributes[index];
    const attributeValidatorName = extractValidatorName(attribute);

    if (attributeValidatorName === validatorName) {
      attributeName = `data-${Utils.camelToKebab(attribute)}`;
      break;
    }
  }

  return attributeName;
}

export function getErrorMessage(input, validatorName) {
  const attributeName = findAttributeName(input, validatorName);
  const errorMessage = input.getAttribute(attributeName);
  return errorMessage;
}

export function dispatchInputChangeEvent(input) {
  const event = document.createEvent('Event');
  event.initEvent('change', true, true);
  input.dispatchEvent(event);
}

export function disableInputs(inputs) {
  inputs.forEach((input) => {
    const inputControl = parents(input, '[data-form="control"]')[0];
    inputControl.classList.add('disabled');
  });
}

export function setInputValidity(input, isValid, validatorName) {
  if (isValid) {
    input.setCustomValidity('');
    input.parentElement.classList.remove(VALIDATION_ERROR_CLASS);
  } else {
    const errorMessage = getErrorMessage(input, validatorName);
    if (errorMessage !== 'true' || errorMessage !== '') {
      setInputValidationMessage(input, errorMessage);
    }
    input.setCustomValidity(INVALID_FIELD_STRING);
    input.parentElement.classList.add(VALIDATION_ERROR_CLASS);
  }
}

export function isCustomValidInput(input) {
  const ignoreUnset = input.dataset.formValidatingOptions && input.dataset.formValidatingOptions.indexOf('ignore-unset') > -1;
  const debugMode = input.dataset.formDebug === 'true';
  const formValidatorNames = getFormValidatorNames(input.dataset);

  if (__DEVELOPMENT__ && debugMode) {
    console.log('JraForm.isCustomValidInput() ', input.id);
    console.log(input);
    formValidatorNames.forEach(name => console.log(`JraForm.isCustomValidInput detected validator: ${name}`));
  }

  // special internal injected rule to abort execution of further validators if input is in a
  // hidden container
  if (JraValidators.ifHiddenAbort(input)) {
    if (__DEVELOPMENT__ && debugMode) {
      console.log('JraForm.isCustomValidInput ifHiddenAbort: true');
    }

    setInputValidity(input, true);
    return;
  }

  // special internal injected rule to abort execution of further validators if input is not
  // required and has no value
  if (!ignoreUnset && formValidatorNames.indexOf('required') === -1 && JraValidators.ifUnsetAbort(input)) {
    if (__DEVELOPMENT__ && debugMode) {
      console.log('JraForm.isCustomValidInput ifUnsetAbort: true');
    }

    setInputValidity(input, true);
    return;
  }

  // proceed through all validators unless one return false
  for (let index = 0; index < formValidatorNames.length; index += 1) {
    let isValid = false;
    const functionName = formValidatorNames[index];

    if (JraValidators[functionName]) {
      if (functionName.toLowerCase().indexOf('async') > -1) { // validator name must have async into to handle it in this way
        input.classList.add('validatedAsync'); // class is added here to allow for error validation feedback before form submission
        isValid = true; // initially set to true until the actual data is returned.
        setInputValidity(input, isValid, functionName);
        JraValidators[functionName](
          input,
          isAsyncValid => setInputValidity(input, isAsyncValid, functionName),
        );
      } else {
        input.classList.remove('validatedAsync'); // remove any previous async validation
        isValid = JraValidators[functionName](input);
        setInputValidity(input, isValid, functionName);
      }
    } else {
      throw new Error(`Validator Error there is no validator for: ${functionName} id: ${input.id}`);
    }

    if (__DEVELOPMENT__ && debugMode) {
      const attributeName = findAttributeName(input, functionName);
      console.log(`JraForm.isCustomValidInput execute validator: ${functionName} -> isValid: ${isValid} message: ${input.getAttribute(attributeName)}`);
    }

    // if any validator is invalid we want to bork out of the every loop immediately and
    // ignore the reset of the validators/
    if (isValid === false) {
      break;
    }
  }
}
